import { useNavigate } from "react-router-dom";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useFloatingActionBar } from "@/lib/hooks/useFloatingActionBar";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  ProductDetailInput,
  ProductFaqInput,
  ProductFaqView,
  ProductHighlightInput,
  ProductHighlightView,
  ProductNoteInput,
  ProductNoteView,
  ProductView,
} from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useUpdateProductMutation } from "@/modules/products/services/useUpdateProductMutation";
import { Alert, AutoDismissNotice, DiscardChangesDialog, FormActionButtons } from "@a2z/ui";
import {
  ProductContentFields,
  type ProductContentDraft,
  type ProductContentValidationErrors,
  type ProductDetailDraft,
  type ProductFaqDraft,
  type ProductHighlightDraft,
  type ProductNoteDraft,
  toProductDetailDraft,
  toProductFaqDraft,
  toProductHighlightDraft,
  toProductNoteDraft,
} from "./ProductContentFields";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";

type FormProps = {
  locale: Locale;
  storeSlug: string;
  productId: string;
  submitLabel?: string;
  onSuccess?: (product: ProductView) => void;
  hideCancel?: boolean;
  /** Suppress all inline action UI (page-level SaveBar takes over). */
  hideActions?: boolean;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  enableNavigationGuard?: boolean;
  defaultValues?: {
    shortDescriptionAr?: string | null;
    shortDescriptionEn?: string | null;
    highlights?: ProductHighlightView[];
    details?: ProductDetailInput[];
    faq?: ProductFaqView[];
    notes?: ProductNoteView[];
  };
};

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const createDraftId = () =>
  globalThis.crypto?.randomUUID?.() ?? `new-${Date.now()}-${Math.random()}`;

const emptyHighlight = (): ProductHighlightDraft => ({
  id: createDraftId(),
  text: { ar: "", en: "" },
});

const emptyDetail = (): ProductDetailDraft => ({
  id: createDraftId(),
  label: { ar: "", en: "" },
  value: { ar: "", en: "" },
});

const emptyFaq = (): ProductFaqDraft => ({
  id: createDraftId(),
  question: { ar: "", en: "" },
  answer: { ar: "", en: "" },
});

const emptyNote = (): ProductNoteDraft => ({
  id: createDraftId(),
  kind: "general",
  text: { ar: "", en: "" },
});

const moveByDirection = <T extends { id: string }>(
  list: T[],
  id: string,
  direction: "up" | "down",
): T[] => {
  const fromIndex = list.findIndex((item) => item.id === id);
  if (fromIndex < 0) {
    return list;
  }
  const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
  if (toIndex < 0 || toIndex >= list.length) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

const serializeDraft = (draft: ProductContentDraft): string => JSON.stringify(draft);

const buildInitialDraft = (
  defaultValues: FormProps["defaultValues"],
): ProductContentDraft => ({
  shortDescriptionAr: defaultValues?.shortDescriptionAr ?? "",
  shortDescriptionEn: defaultValues?.shortDescriptionEn ?? "",
  highlights: (defaultValues?.highlights ?? []).map((item) =>
    toProductHighlightDraft(item, createDraftId()),
  ),
  details: (defaultValues?.details ?? []).map((item) =>
    toProductDetailDraft(item, createDraftId()),
  ),
  faq: (defaultValues?.faq ?? []).map((item) =>
    toProductFaqDraft(item, createDraftId()),
  ),
  notes: (defaultValues?.notes ?? []).map((item) =>
    toProductNoteDraft(item, createDraftId()),
  ),
});

function normalizeHighlights(
  items: ProductHighlightDraft[],
): { value: ProductHighlightInput[]; invalid: boolean } {
  const value: ProductHighlightInput[] = [];
  let invalid = false;

  for (const item of items) {
    const ar = item.text.ar.trim();
    const en = trimOrNull(item.text.en);
    const used = ar.length > 0 || Boolean(en);
    if (!used) {
      continue;
    }
    if (!ar) {
      invalid = true;
      continue;
    }
    value.push({ text: { ar, en } });
  }

  return { value, invalid };
}

function normalizeDetails(
  items: ProductDetailDraft[],
): { value: ProductDetailInput[]; invalid: boolean } {
  const value: ProductDetailInput[] = [];
  let invalid = false;

  for (const item of items) {
    const labelAr = item.label.ar.trim();
    const labelEn = trimOrNull(item.label.en);
    const valueAr = item.value.ar.trim();
    const valueEn = trimOrNull(item.value.en);
    const used =
      labelAr.length > 0 ||
      valueAr.length > 0 ||
      Boolean(labelEn) ||
      Boolean(valueEn);
    if (!used) {
      continue;
    }
    if (!labelAr || !valueAr) {
      invalid = true;
      continue;
    }
    value.push({
      label: { ar: labelAr, en: labelEn },
      value: { ar: valueAr, en: valueEn },
    });
  }

  return { value, invalid };
}

function normalizeFaq(
  items: ProductFaqDraft[],
): { value: ProductFaqInput[]; invalid: boolean } {
  const value: ProductFaqInput[] = [];
  let invalid = false;

  for (const item of items) {
    const questionAr = item.question.ar.trim();
    const questionEn = trimOrNull(item.question.en);
    const answerAr = item.answer.ar.trim();
    const answerEn = trimOrNull(item.answer.en);
    const used =
      questionAr.length > 0 ||
      answerAr.length > 0 ||
      Boolean(questionEn) ||
      Boolean(answerEn);
    if (!used) {
      continue;
    }
    if (!questionAr || !answerAr) {
      invalid = true;
      continue;
    }
    value.push({
      question: { ar: questionAr, en: questionEn },
      answer: { ar: answerAr, en: answerEn },
    });
  }

  return { value, invalid };
}

function normalizeNotes(
  items: ProductNoteDraft[],
): { value: ProductNoteInput[]; invalid: boolean } {
  const value: ProductNoteInput[] = [];
  let invalid = false;

  for (const item of items) {
    const ar = item.text.ar.trim();
    const en = trimOrNull(item.text.en);
    const used = ar.length > 0 || Boolean(en);
    if (!used) {
      continue;
    }
    if (!ar) {
      invalid = true;
      continue;
    }
    value.push({
      kind: item.kind,
      text: { ar, en },
    });
  }

  return { value, invalid };
}

const emptyValidationErrors: ProductContentValidationErrors = {};

export function ProductContentForm({
  locale,
  storeSlug,
  productId,
  submitLabel,
  onSuccess,
  hideCancel = false,
  hideActions = false,
  onCancel,
  onDirtyChange,
  enableNavigationGuard = true,
  defaultValues,
}: FormProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const errorRef = useRef<HTMLDivElement | null>(null);
  const actionRef = useRef<HTMLDivElement | null>(null);
  const formId = useId();
  const [savedDraft, setSavedDraft] = useState<ProductContentDraft>(() =>
    buildInitialDraft(defaultValues),
  );
  const [draft, setDraft] = useState<ProductContentDraft>(() => savedDraft);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] =
    useState<ProductContentValidationErrors>(emptyValidationErrors);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const saveLabel = submitLabel ?? t(keys.common.save);
  const savedSignature = useMemo(() => serializeDraft(savedDraft), [savedDraft]);
  const draftSignature = useMemo(() => serializeDraft(draft), [draft]);
  const isDirty = draftSignature !== savedSignature;
  const shouldFloatActions = useFloatingActionBar(actionRef, isDirty && !hideCancel);
  const { mutateAsync: updateProduct } = useUpdateProductMutation();

  const resetToSaved = () => {
    setDraft(savedDraft);
    setValidationErrors(emptyValidationErrors);
    setError(null);
    setShowSuccess(false);
  };

  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty,
    onDiscard: resetToSaved,
    enableNavigationGuard,
  });
  const guard = useNavigationGuard();
  const discardPrompt = guard?.promptDiscard ?? promptDiscard;

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleCancel = () => {
    discardPrompt(() => {
      resetToSaved();
      onCancel?.();
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // The edit page submits every section form at once; skip this one when it
    // has no changes so a clean section never triggers a no-op save or toast.
    if (!isDirty) {
      return;
    }
    setIsSubmitting(true);
    setShowSuccess(false);
    setError(null);
    setValidationErrors(emptyValidationErrors);

    const normalizedHighlights = normalizeHighlights(draft.highlights);
    const normalizedDetails = normalizeDetails(draft.details);
    const normalizedFaq = normalizeFaq(draft.faq);
    const normalizedNotes = normalizeNotes(draft.notes);

    const nextValidationErrors: ProductContentValidationErrors = {};
    if (normalizedHighlights.invalid) {
      nextValidationErrors.highlights = t(keys.errors.required);
    }
    if (normalizedDetails.invalid) {
      nextValidationErrors.details = t(keys.errors.required);
    }
    if (normalizedFaq.invalid) {
      nextValidationErrors.faq = t(keys.errors.required);
    }
    if (normalizedNotes.invalid) {
      nextValidationErrors.notes = t(keys.errors.required);
    }

    if (
      nextValidationErrors.highlights ||
      nextValidationErrors.details ||
      nextValidationErrors.faq ||
      nextValidationErrors.notes
    ) {
      setValidationErrors(nextValidationErrors);
      setError(t(keys.errors.required));
      setIsSubmitting(false);
      return;
    }

    const nextShortDescriptionAr = trimOrNull(draft.shortDescriptionAr);
    const nextShortDescriptionEn = trimOrNull(draft.shortDescriptionEn);
    const savedShortDescriptionAr = trimOrNull(savedDraft.shortDescriptionAr);
    const savedShortDescriptionEn = trimOrNull(savedDraft.shortDescriptionEn);

    const payload: Parameters<typeof updateProduct>[0] = {
      storeSlug,
      productId,
      highlights: normalizedHighlights.value,
      details: normalizedDetails.value,
      faq: normalizedFaq.value,
      notes: normalizedNotes.value,
    };
    if (nextShortDescriptionAr !== savedShortDescriptionAr) {
      payload.shortDescriptionAr = nextShortDescriptionAr;
    }
    if (nextShortDescriptionEn !== savedShortDescriptionEn) {
      payload.shortDescriptionEn = nextShortDescriptionEn;
    }

    let updatedProduct: ProductView;
    try {
      updatedProduct = await updateProduct(payload);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error ? caughtError.message : t(keys.errors.generic),
      );
      setIsSubmitting(false);
      return;
    }

    setSavedDraft(draft);
    setValidationErrors(emptyValidationErrors);
    setShowSuccess(true);
    setIsSubmitting(false);

    if (onSuccess) {
      onSuccess(updatedProduct);
      return;
    }

    navigate(
      `${getSellerPath(locale, storeSlug)}/products?notice=product_saved`,
      { replace: true }
    );
  };

  return (
    <>
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-6">
        {error ? (
          <div ref={errorRef}>
            <Alert message={error} helperText={t(keys.errors.recovery)} />
          </div>
        ) : null}
        {showSuccess ? (
          <AutoDismissNotice title={t(keys.seller.productSavedShare)} />
        ) : null}
        <ProductContentFields
          draft={draft}
          disabled={isSubmitting}
          errors={validationErrors}
          onShortDescriptionChange={(field, value) => {
            setDraft((current) => ({ ...current, [field]: value }));
            setShowSuccess(false);
          }}
          onAddHighlight={() => {
            setDraft((current) => ({
              ...current,
              highlights: [...current.highlights, emptyHighlight()],
            }));
            setShowSuccess(false);
          }}
          onMoveHighlight={(id, direction) => {
            setDraft((current) => ({
              ...current,
              highlights: moveByDirection(current.highlights, id, direction),
            }));
            setShowSuccess(false);
          }}
          onRemoveHighlight={(id) => {
            setDraft((current) => ({
              ...current,
              highlights: current.highlights.filter((item) => item.id !== id),
            }));
            setShowSuccess(false);
          }}
          onHighlightChange={(id, field, value) => {
            setDraft((current) => ({
              ...current,
              highlights: current.highlights.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      text: { ...item.text, [field]: value },
                    }
                  : item,
              ),
            }));
            setShowSuccess(false);
          }}
          onAddDetail={() => {
            setDraft((current) => ({
              ...current,
              details: [...current.details, emptyDetail()],
            }));
            setShowSuccess(false);
          }}
          onMoveDetail={(id, direction) => {
            setDraft((current) => ({
              ...current,
              details: moveByDirection(current.details, id, direction),
            }));
            setShowSuccess(false);
          }}
          onRemoveDetail={(id) => {
            setDraft((current) => ({
              ...current,
              details: current.details.filter((item) => item.id !== id),
            }));
            setShowSuccess(false);
          }}
          onDetailChange={(id, part, field, value) => {
            setDraft((current) => ({
              ...current,
              details: current.details.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      [part]: { ...item[part], [field]: value },
                    }
                  : item,
              ),
            }));
            setShowSuccess(false);
          }}
          onAddFaq={() => {
            setDraft((current) => ({
              ...current,
              faq: [...current.faq, emptyFaq()],
            }));
            setShowSuccess(false);
          }}
          onMoveFaq={(id, direction) => {
            setDraft((current) => ({
              ...current,
              faq: moveByDirection(current.faq, id, direction),
            }));
            setShowSuccess(false);
          }}
          onRemoveFaq={(id) => {
            setDraft((current) => ({
              ...current,
              faq: current.faq.filter((item) => item.id !== id),
            }));
            setShowSuccess(false);
          }}
          onFaqChange={(id, part, field, value) => {
            setDraft((current) => ({
              ...current,
              faq: current.faq.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      [part]: { ...item[part], [field]: value },
                    }
                  : item,
              ),
            }));
            setShowSuccess(false);
          }}
          onAddNote={() => {
            setDraft((current) => ({
              ...current,
              notes: [...current.notes, emptyNote()],
            }));
            setShowSuccess(false);
          }}
          onMoveNote={(id, direction) => {
            setDraft((current) => ({
              ...current,
              notes: moveByDirection(current.notes, id, direction),
            }));
            setShowSuccess(false);
          }}
          onRemoveNote={(id) => {
            setDraft((current) => ({
              ...current,
              notes: current.notes.filter((item) => item.id !== id),
            }));
            setShowSuccess(false);
          }}
          onNoteChange={(id, field, value) => {
            setDraft((current) => ({
              ...current,
              notes: current.notes.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      text: { ...item.text, [field]: value },
                    }
                  : item,
              ),
            }));
            setShowSuccess(false);
          }}
          onNoteKindChange={(id, kind) => {
            setDraft((current) => ({
              ...current,
              notes: current.notes.map((item) =>
                item.id === id ? { ...item, kind } : item,
              ),
            }));
            setShowSuccess(false);
          }}
        />
        <p className="text-xs text-[color:var(--text-muted)]">
          {t(keys.products.contentSaveHelper)}
        </p>
        {hideActions ? (
          <div ref={actionRef} aria-hidden="true" className="h-0 w-0" />
        ) : (
          <div ref={actionRef} className="mt-2">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              hideCancel={hideCancel}
            />
          </div>
        )}
      </form>
      {!hideActions && shouldFloatActions ? (
        <div
          className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 translate-y-0 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 opacity-100 shadow-[var(--shadow-sm)] transition-all duration-300 ease-out lg:bottom-0"
        >
          <div className="mx-auto max-w-[640px]">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              hideCancel={hideCancel}
              centered
            />
          </div>
        </div>
      ) : null}
      {!guard ? (
        <DiscardChangesDialog
          {...discardDialogState}
          title={t(keys.forms.discardChangesTitle)}
          body={t(keys.forms.discardChangesBody)}
          discardLabel={t(keys.forms.discardChangesDiscard)}
          continueLabel={t(keys.forms.discardChangesContinue)}
        />
      ) : null}
    </>
  );
}
